import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  inject,
  INJECTOR,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ApplicationModule,
  backNavigation,
  CommandStore,
  menuItems,
  showRequestErrors,
  SOFTLINE_FEATURE_COMMANDS,
  subtitle,
  title,
} from '@softline/application';
import { AuthorizationStore, SOFTLINE_FEATURE_AUTHORIZATION } from '@softline/auth';
import { base64Encode, SOFTLINE_SERVICE_UUID, Store } from '@softline/core';
import {
  MessageBarStore,
  ModalStore,
  SOFTLINE_FEATURE_MESSAGE_BAR,
  SOFTLINE_FEATURE_MODAL,
  UiCoreModule,
} from '@softline/ui-core';
import {
  BehaviorSubject,
  combineLatestWith,
  filter,
  firstValueFrom,
  from,
  map,
  Observable,
  switchMap,
} from 'rxjs';
import { SOFTLINE_PERMISSION_WERKSTAETTENAUFTRAG_ARBEITSBERICHT_ERSTELLEN } from '../../auftrag.permissions';
import {
  SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
  SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARTIKEL,
  SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
} from '../../auftrag.shared';
import { CloseTaskCommand } from '../../commands/close-task.command';
import { ArbeitsberichtCardComponent } from '../../components/arbeitsbericht-card/arbeitsbericht-card.component';
import {
  ArtikelData,
  AuftragArtikelCardComponent,
} from '../../components/auftrag-artikel-card/auftrag-artikel-card.component';
import { AuftragCardComponent } from '../../components/auftrag-card/auftrag-card.component';
import { Auftrag } from '../../data/auftrag';
import { AuftragArtikel } from '../../data/auftrag-artikel';
import { Kunde } from '../../data/kunde';
import { Arbeitsbericht } from '../../data/stempel';
import { ArtikelOptionDialogComponent } from '../../dialogs/artikel-option-dialog/artikel-option-dialog.component';
import { StartArbeitsberichtDialogComponent } from '../../dialogs/start-arbeitsbericht-dialog/start-arbeitsbericht-dialog.component';
import { GetSignaturePipe } from '../../pipes/get-signature.pipe';
import { WerkstattArbeitsberichtStore } from '../../store/werkstatt-arbeitsbericht.store';
import * as WerkstattAuftraegeStore from '../../store/werkstatt-auftraege.store';
import { WerkstattAuftragArtikelStore } from '../../store/werkstatt-auftrag-artikel.store';
import { createAuftragCommands } from './auftrag.commands';
import { PageComponent } from '@softapps/core';

const BASE_GOOGLE_URL = 'https://www.google.com/maps/search/?api=1&query=';

@Component({
  selector: 'soft-garage-order',
  imports: [
    CommonModule,
    UiCoreModule,
    ApplicationModule,
    AuftragCardComponent,
    AuftragArtikelCardComponent,
    ArbeitsberichtCardComponent,
    GetSignaturePipe,
    PageComponent
  ],
  templateUrl: './auftrag.component.html',
  styleUrls: ['./auftrag.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuftragComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly injector = inject(INJECTOR);
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private uuid = inject(SOFTLINE_SERVICE_UUID);
  private readonly document: Document = inject(DOCUMENT);

  private GOOGLE_LINK = BASE_GOOGLE_URL;

  readonly isSigning$ = new BehaviorSubject(false);

  readonly id$ = this.activatedRoute.params.pipe(
    map((o) => +o['id']),
    filter((o) => !!o)
  );

  readonly id = toSignal(this.id$);

  readonly processingArtikelIndex$ = new BehaviorSubject<number | null>(null);

  readonly auftrag$: Observable<Auftrag> = this.id$.pipe(
    switchMap((id) =>
      from(this.load({ id, once: true })).pipe(
        switchMap(() =>
          this.store.observe(
            SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
            WerkstattAuftraegeStore.getters.entity,
            id
          )
        )
      )
    )
  );

  private readonly alleArbeitsberichte$: Observable<Arbeitsbericht[]> =
    this.store.observe(
      SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
      WerkstattArbeitsberichtStore.getters.all
    ) as Observable<Arbeitsbericht[]>;

  readonly arbeitsberichte$: Observable<Arbeitsbericht[]> =
    this.alleArbeitsberichte$.pipe(
      map((o) => o as Arbeitsbericht[]),
      combineLatestWith(this.id$),
      map(([reports, orderId]) => reports.filter((o) => o.orderId === orderId))
    );

  readonly arbeitsberichteLoading$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
    WerkstattArbeitsberichtStore.getters.loading
  );

  readonly arbeitsberichtLoading = toSignal(this.arbeitsberichteLoading$);

  readonly auftragArtikel$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARTIKEL,
    WerkstattAuftragArtikelStore.getters.allGrouped
  );

  readonly auftragArtikelLoading$ = this.store.observe(
    SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARTIKEL,
    WerkstattAuftragArtikelStore.getters.loading
  );

  constructor() {
    title('Werkstätten Aufträge');
    subtitle('Auftragsdetails');
    backNavigation(['werkstaettenauftrag']);
    menuItems(() => [
      {
        name: 'close-order',
        outlet: 'bottom',
        title: '#WORKSHOP.TASK_DETAILS.CLOSE',
        icon: 'fa-regular fa-check',
        type: 'execute',
        class: 'soft-button primary',
        execute: async () => {
          const task = await firstValueFrom(this.auftrag$);
          await this.closeTask(task);
        },
        canExecute: this.arbeitsberichtLoading() === false,
      },
      {
        name: 'list',
        title: '#WORKSHOP.TASK_OUTLINE.TITLE',
        icon: 'fa-regular fa-list',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag'],
      },
      {
        name: 'order-detail',
        title: 'Auftragsdetails',
        icon: 'fa-regular fa-garage',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag', this.id()],
      },
      {
        name: 'create-order',
        title: '#WORKSHOP.COMPONENTS.CREATE_TASK.TITLE',
        icon: 'fa-regular fa-pen-to-square',
        type: 'route',
        outlet: 'nav',
        routerLink: ['/werkstaettenauftrag', 'erstellen'],
      },
    ]);
  }

  async ngOnInit(): Promise<void> {
    const id = this.activatedRoute.snapshot.params['id'];

    this.store.commit(SOFTLINE_FEATURE_COMMANDS, CommandStore.mutations.addSet, {
      name: AuftragComponent,
      commands: createAuftragCommands({
        store: this.store,
        router: this.router,
        injector: this.injector,
        getTask: () =>
          this.store.get(
            SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
            WerkstattAuftraegeStore.getters.entity,
            id
          ),
        permission: this.store.get(
          SOFTLINE_FEATURE_AUTHORIZATION,
          AuthorizationStore.getters.permissions
        ),
        timeReport: this.store.get(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
          WerkstattArbeitsberichtStore.getters.active
        ),
      }),
    });
  }

  ngAfterViewInit(): void {
    const element = this.document.getElementsByClassName('content')[0];
    if (!element) return;
    element.scrollTo({ top: 0, left: 0 });
  }

  ngOnDestroy() {
    this.store.commit(
      SOFTLINE_FEATURE_COMMANDS,
      CommandStore.mutations.removeSet,
      AuftragComponent
    );
  }

  getGoogleLink(customer: Kunde | undefined): string {
    if (!customer) return '';
    this.GOOGLE_LINK = BASE_GOOGLE_URL;
    this.GOOGLE_LINK += customer.strasse
      ? customer.strasse + (customer.strassenr ? customer.strassenr : '') + ','
      : '';
    this.GOOGLE_LINK += customer.plz ? customer.plz + ',' : '';
    this.GOOGLE_LINK += customer.ort ? customer.ort + ',' : '';
    return encodeURI(this.GOOGLE_LINK.slice(0, -1));
  }

  async navigateToAddTaskItems(auftragId: number): Promise<void> {
    await this.router.navigate(['/werkstaettenauftrag', auftragId, 'artikel']);
  }

  async navigateToManualWorkReport(auftrag: Auftrag): Promise<void> {
    const isAuthorized = this.store.get(
      SOFTLINE_FEATURE_AUTHORIZATION,
      AuthorizationStore.getters.authorized,
      SOFTLINE_PERMISSION_WERKSTAETTENAUFTRAG_ARBEITSBERICHT_ERSTELLEN
    );
    if (isAuthorized) {
      await this.router.navigate(['/werkstaettenauftrag', auftrag.id, 'arbeitsbericht']);
    } else {
      await this.presentArbeitsberichtDialog(auftrag);
    }
  }

  async closeTask(auftrag: Auftrag): Promise<void> {
    await new CloseTaskCommand(this.store, () => auftrag, this.router).execute();
  }

  async signTask(auftrag: Auftrag, file: Blob | null | undefined): Promise<void> {
    this.isSigning$.next(true);
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
        WerkstattAuftraegeStore.actions.sign,
        {
          id: auftrag.id,
          signature: { files: [file] },
        }
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: '#WORKSHOP.MESSAGES.SUCCESS.TITLE',
          message: '#WORKSHOP.MESSAGES.SUCCESS.SIGN',
        }
      );
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          duration: 10000,
          title: '#WORKSHOP.DIALOGS.ERROR_SUBJECT',
          message: '#WORKSHOP.DIALOGS.SIGNATURE.ERROR',
        }
      );
    } finally {
      this.isSigning$.next(false);
    }
  }

  async moreTapped(item: ArtikelData, auftrag: Auftrag, index: number): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<'delete' | 'addAgain' | null, { item: ArtikelData }>(),
      {
        component: ArtikelOptionDialogComponent,
        dismiss: true,
        data: { item },
      }
    );
    if (!result || result === 'DISMISSED') return;
    this.processingArtikelIndex$.next(index);
    if (result === 'delete') {
      try {
        await this.deleteTaskItems(item?.entities || []);
      } catch (e) {
        this.processingArtikelIndex$.next(null);
        showRequestErrors(this.store, e);
      }
    } else if (result === 'addAgain') {
      await this.router.navigate(['/werkstaettenauftrag', auftrag.id, 'artikel'], {
        queryParams: { prefill: base64Encode(JSON.stringify(item?.artikel)) },
      });
    }
    this.processingArtikelIndex$.next(null);
  }

  async deleteTaskItems(artikel: AuftragArtikel[]): Promise<void> {
    try {
      await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARTIKEL,
        WerkstattAuftragArtikelStore.actions.removeArtikel,
        artikel
      );

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        { title: '#WORKSHOP.TASK_DETAILS.DELETE.ITEM.SUCCESS' }
      );
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        { title: '#WORKSHOP.TASK_DETAILS.DELETE.ITEM.ERROR' }
      );
    } finally {
      this.processingArtikelIndex$.next(null);
    }
  }

  async deleteArbeitsbericht(arbeitsbericht: Arbeitsbericht): Promise<void> {
    try {
      const changes = this.store.get(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
        WerkstattArbeitsberichtStore.getters.changes
      );
      if (changes?.length > 0) {
        this.store.commit(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
          WerkstattArbeitsberichtStore.mutations.resetChanges
        );
      }
      await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
        WerkstattArbeitsberichtStore.actions.prepareDelete,
        arbeitsbericht
      );
      await this.store.dispatch(
        SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
        WerkstattArbeitsberichtStore.actions.saveAll,
        { pathParams: {}, token: this.uuid() }
      );
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }

  private async presentArbeitsberichtDialog(auftrag: Auftrag): Promise<void> {
    await this.store.dispatch(SOFTLINE_FEATURE_MODAL, ModalStore.actions.open(), {
      id: 'START_ACTIVITY',
      component: StartArbeitsberichtDialogComponent,
      data: { auftrag },
      dismiss: { button: true, backdrop: true },
    });
  }

  private async load({ id, once }: { id: number; once: boolean }): Promise<void> {
    try {
      await Promise.all([
        this.store.dispatch(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_AUFTRAEGE,
          WerkstattAuftraegeStore.actions[once ? 'loadOnce' : 'load'],
          { id }
        ),
        this.store.dispatch(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARBEITSBERICHT,
          WerkstattArbeitsberichtStore.actions.loadMany,
          { clear: true, queryParams: { orderId: id } }
        ),
        this.store.dispatch(
          SOFTLINE_FEATURE_WERKSTAETTENAUFTRAG_ARTIKEL,
          WerkstattAuftragArtikelStore.actions.loadMany,
          { clear: true, queryParams: { orderId: id } }
        ),
      ]);
    } catch (e) {
      showRequestErrors(this.store, e);
    }
  }
}
